/**
 * @file   src\Pages\SupportChat.tsx
 * @brief  Support Chat page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Container, Button, Row, Col, useOutletContext, useEffect, useState, useParams, Modal, React } from '../utils/thirdpartyComponents';
import { AppContextType, TicketDetailItem } from '../interfaces/generalInterface';
import CWSelect from '../components/CWSelect';
import CWTextarea from '../components/CWTextarea';
import AvatarStaff from '../assets/images/avatar-staff.svg';
import AvatarCustomer from '../assets/images/avatar-customer.svg';
import { pageURLs } from '../utils/pageUrls';
import axios from '../config/axiosConfig';
import { TicketApi } from '../utils/apiUrls';
import { MessageToaster } from '../utils/toastUtil';
import { getFromLocalStorage } from '../utils/helper';
import SmallLoader from '../components/SmallLoader';
import SyncIcon from '../assets/images/icons/syncicon';

const SupportChat = () => {

    const [options, setOptions] = useState([
        { label: 'Open', value: 1 },
        { label: 'Closed', value: 2 },
        { label: 'Re-Open', value: 3 }
    ]);

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
    const param = useParams();
    const toaster = new MessageToaster()
    const [loading, setLoading] = useState(true);
    const [ticketData, setTicketData] = useState<TicketDetailItem>({
        chatList: [],
        ticketDetails: {
            editedOn: '',
            locationID: '',
            locationName: '',
            message: '',
            phone: '',
            status: 0,
            subject: '',
            ticketNumber: ''
        }
    })

    const [formData, setFormData] = useState({
        message: ''
    })

    // Handle errors
    const [errors, setErrors] = useState({
        message: ''
    });

    const [previousStatus, setPreviousStatus] = useState(ticketData.ticketDetails.status); // Track original status

    const [showChangeStatusConfirmation, setShowChangeStatusConfirmation] = useState(false)

    const [selectedOption, setSelectedOption] = useState(ticketData.ticketDetails?.status || 0);

    const [statusUpdated, setStatusUpdated] = useState(false)

    // get user details from session
    const storageData = getFromLocalStorage('CARWASH_ADMIN_DATA');
    const userInfo = storageData && storageData !== null ? storageData : null;

    const [chatEntered, setChatEntered] = useState(false)

    useEffect(() => {
        if (ticketData.ticketDetails) {
            setSelectedOption(ticketData.ticketDetails.status);
        }
    }, [ticketData.ticketDetails]);


    // To display heading
    useEffect(() => {
        handleUpdate()
        getTicketDetails()
    }, [chatEntered, statusUpdated])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Customer Support Desk", url: `/superadmin/${pageURLs.customer_support_desk}`, showBackButton: true, showMainMenu: true, activeLink: "manage-car-wash" });
    };

    // Triggers on entering input
    const handleInputChange = async (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target;

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value.trim(),
        });

    };

    const formatLastUpdatedDate = (lastUpdatedDate: string) => {
        const isoDate = lastUpdatedDate;
        const date = new Date(isoDate);

        // Convert to desired format
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        };

        const formattedDate = date.toLocaleString('en-US', options);
        return formattedDate
    }

    const formatChatDate = (chatDate: string) => {
        const isoDate = chatDate;
        const date = new Date(isoDate);

        // Convert to desired format
        const options: Intl.DateTimeFormatOptions = {
            // Convert to desired format
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true

        };

        const formattedChatDate = date.toLocaleString('en-US', options);
        return formattedChatDate
    }

    // Get Ticket Details
    const getTicketDetails = async () => {
        setChatEntered(false)
        try {
            const { data } = await axios.get(TicketApi.ADMIN_GET_TICKET_DETAILS + param.supportId)

            if (data.error === 0) {

                const ticketDetail = data.data

                // Ticket status options will vary according to current ticket status
                if (ticketDetail.ticketDetails.status === 1) {
                    setOptions([{ label: 'Open', value: 1 },
                    { label: 'Closed', value: 2 },
                    ])
                } else {
                    setOptions([
                        { label: 'Closed', value: 2 },
                        { label: 'Re-Open', value: 3 }])
                }
                setTicketData(ticketDetail)
                setLoading(false)
            }

            else {
                // show error message
                toaster.toastError(data.message);
                setLoading(false)
            }
        } catch (error: any) {
            console.error('Error fetching ticket information:', error.message);
            throw new Error(error.response?.data?.message || error.message);

        } finally {
            setLoading(false)
        }
    }

    // On status update show confirmation
    const changeStatus = async (selectedOption: any) => {
        if (selectedOption.value !== ticketData.ticketDetails.status) {
            setPreviousStatus(ticketData.ticketDetails.status);
            setSelectedOption(selectedOption.value)
            setStatusUpdated(false)
            setShowChangeStatusConfirmation(true)
        }
    }

    //Hide ppopup
    const handleOnHide = () => {
        setSelectedOption(previousStatus); // Revert to the original status
        setShowChangeStatusConfirmation(false)
    }

    const onStatusUpdate = async () => {
        try {
            setLoading(true)
            const { data } = await axios.post(TicketApi.ADMIN_UPDATE_TICKET_STATUS, { supportId: param.supportId ? parseInt(param.supportId) : 0, status: selectedOption })

            if (data?.error === 0) {
                // show success message
                toaster.toastSuccess(data?.message);
                setStatusUpdated(true)
                setShowChangeStatusConfirmation(false)
            }
            else {
                // show error message
                toaster.toastError(data?.message);
            }
        } catch (error: any) {
            throw new Error(error?.response?.data?.message || error?.message);
        }
        finally {
            setLoading(false); // Hide loader
        }
    }

    // On submitting message
    const chatSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        try {
            const { data } = await axios.post(TicketApi.ADMIN_POST_MESSAGE, {
                supportId: param.supportId ? parseInt(param.supportId) : 0,
                message: formData.message
            })

            if (data?.error === 0) {
                setChatEntered(true)
                setFormData({ message: "" })
            }
            else {
                // show error message
                toaster.toastError(data?.message);
            }
        } catch (error: any) {
            throw new Error(error?.response?.data?.message || error?.message);
        }
    }

    return (

        <div className='support-chat-wrap'>
            <Container>
                <div className="support-status-block d-flex align-items-center justify-content-between">
                    <h6><span className='f-semi'>Ticket Number:</span> {ticketData.ticketDetails.ticketNumber}</h6>
                    <h6><span className='f-semi'>Car Wash Location:</span>{ticketData.ticketDetails.locationName}</h6>
                    <h6><span className='f-semi'>Phone Number:</span> {ticketData.ticketDetails.phone}</h6>
                    <CWSelect
                        id="status"
                        name="status"
                        options={options}
                        dropdownGap={0}
                        dropdownPosition="auto"
                        values={options.filter(option => option.value === selectedOption)}
                        multi={false}
                        searchable={false}
                        onChange={(selectedOptions) => changeStatus(selectedOptions[0])}
                        label="Ticket Status"
                    />
                </div>
                <Row>
                    <Col md={8}>
                        <div className="message-history">
                            <div className="title-sec d-flex align-items-center justify-content-between"><h6 className='f-bold'>Message History</h6>
                                <Button variant="outline" className="d-flex align-items-center secondary-btn btn-refresh" onClick={getTicketDetails}><SyncIcon /> Refresh Chats</Button>
                            </div>
                            <div className="message-wrap">
                                {ticketData.chatList.map((chat) => (
                                    <div className="message-block scroll-custom d-flex flex-column" key={chat.chatID}>

                                        {chat.createdBy === userInfo.userId ?
                                            <div className="message-group customer-chat d-flex">
                                                <div className="profile-pic"><img src={AvatarCustomer} alt="Car Wash App" /></div>
                                                <div className="chat-sec">
                                                    <div className="bubble customer">
                                                        {chat.message}
                                                    </div>
                                                    <p>{formatChatDate(chat.createdOn)}</p>
                                                </div>
                                            </div> :
                                            <div className="message-group d-flex">
                                                <div className="profile-pic"><img src={AvatarStaff} alt="Car Wash App" /></div>
                                                <div className="chat-sec">
                                                    <div className="bubble staff">
                                                        {chat.message}
                                                    </div>
                                                    <p>{formatChatDate(chat.createdOn)}</p>
                                                </div>
                                            </div>
                                        }
                                    </div>
                                ))}

                                <div className="reply position-relative">
                                    <form onSubmit={chatSubmit}>
                                        <CWTextarea
                                            id="message"
                                            name="message"
                                            type="text"
                                            value={formData.message}
                                            placeholder="Reply"
                                            onChange={handleInputChange}
                                        />
                                        <Button title="Submit" variant="primary" className="primary-btn" type="submit">Submit</Button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="support-details h-100">
                            <div className="info">
                                <p className='title f-semi'>Last Update Time</p>
                                <p>{formatLastUpdatedDate(ticketData.ticketDetails.editedOn)}</p>
                            </div>
                            <div className="info">
                                <p className='title f-semi'>Subject</p>
                                <p>{ticketData.ticketDetails.subject}</p>
                            </div>
                            <div className="info">
                                <p className='title f-semi'>Details</p>
                                <p>{ticketData.ticketDetails.message}</p>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container >

            <Modal
                show={showChangeStatusConfirmation}
                onHide={handleOnHide}
                centered
                className='add-modal'
            >
                <Modal.Body className='text-center'>
                    <h5 className='f-bold text-center'>Are you sure you want to change Ticket status?</h5>
                    <div className="btn-sec d-grid">
                        <Button title='Cancel' variant="light" className="secondary-btn" onClick={handleOnHide}>Cancel</Button>
                        <Button title='Yes' variant="primary" className='primary-btn btn-main m-0' onClick={onStatusUpdate}>Yes</Button>
                    </div>
                </Modal.Body>
                {loading && <SmallLoader />}
            </Modal>
        </div >
    )
}

export default SupportChat;
